import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  SPEED_FACTORS,
  resolveAudioUrl,
  calculateInterpolatedScrollTop,
  evaluateAutoplayStep,
  extractSectionPlainText,
  buildStorySteps,
  findBestNorwegianVoice,
} from "./StoryAutoplayEngine";
import {
  getVoicePreference,
  setVoicePreference,
  isModelCached,
  MODEL_CACHED_STORAGE_KEY,
  downloadNorwegianModel,
  synthesizeNorwegianSpeech,
  defaultVitsLoader,
} from "./PiperTtsEngine";
import { useProgressState } from "../progress/progressContext";

export const useAudioAutoplay = ({
  story,
  currentSectionIndex = 0,
  onSectionChange = () => {},
  vitsLoader = defaultVitsLoader,
}) => {
  const { locks = [] } = useProgressState() || { locks: [] };
  const sections = useMemo(() => story?.sections || [], [story?.sections]);

  const [activeSectionIndex, setActiveSectionIndex] = useState(currentSectionIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoplayActive, setIsAutoplayActive] = useState(false);
  const [speed, setSpeed] = useState("normal");
  const [currentContainerIndex, setCurrentContainerIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume] = useState(1.0);
  const [isMuted, setIsMuted] = useState(false);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [status, setStatus] = useState("IDLE");
  const [mediaError, setMediaError] = useState(null);
  const [activeSpokenText, setActiveSpokenText] = useState("");
  const [voicePreference, setVoicePreferenceState] = useState(() =>
    getVoicePreference()
  );
  const [modelCached, setModelCached] = useState(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        return window.localStorage.getItem(MODEL_CACHED_STORAGE_KEY) === "true";
      } catch {
        return false;
      }
    }
    return false;
  });
  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [voices, setVoices] = useState(() => {
    if (typeof window !== "undefined" && window.speechSynthesis?.getVoices) {
      return window.speechSynthesis.getVoices();
    }
    return [];
  });

  const narrationAudioRef = useRef(null);
  const ttsAudioRef = useRef(null);
  const activeVideoRef = useRef(null);
  const activeObjectUrlRef = useRef(null);
  const abortControllerRef = useRef(null);
  const utteranceRef = useRef(null);
  const timerRef = useRef(null);
  const watchdogTimerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const rafRef = useRef(null);
  const prevLocksLengthRef = useRef(locks.length);

  const vitsLoaderRef = useRef(vitsLoader);
  useEffect(() => {
    vitsLoaderRef.current = vitsLoader;
  }, [vitsLoader]);

  const voicePreferenceRef = useRef(voicePreference);
  useEffect(() => {
    voicePreferenceRef.current = voicePreference;
  }, [voicePreference]);

  const modelCachedRef = useRef(modelCached);
  useEffect(() => {
    modelCachedRef.current = modelCached;
  }, [modelCached]);

  // Check initial cache status asynchronously
  useEffect(() => {
    let mounted = true;
    isModelCached().then((cached) => {
      if (mounted && cached !== modelCachedRef.current) {
        setModelCached(cached);
        modelCachedRef.current = cached;
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const updateVoicePreference = useCallback((pref) => {
    setVoicePreference(pref);
    setVoicePreferenceState(pref);
    voicePreferenceRef.current = pref;
  }, []);

  const onSectionChangeRef = useRef(onSectionChange);
  useEffect(() => {
    onSectionChangeRef.current = onSectionChange;
  }, [onSectionChange]);

  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const locksRef = useRef(locks);
  useEffect(() => {
    locksRef.current = locks;
  }, [locks]);

  const voicesRef = useRef(voices);
  useEffect(() => {
    voicesRef.current = voices;
  }, [voices]);

  const speedRef = useRef(speed);
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  const volumeRef = useRef(volume);
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  const isMutedRef = useRef(isMuted);
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const stepTokenRef = useRef(0);

  // Load and listen to speech synthesis voices
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;

    const updateVoices = () => {
      const v = window.speechSynthesis.getVoices();
      if (v && v.length > 0) {
        setVoices(v);
      }
    };

    updateVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = updateVoices;
    }
    window.speechSynthesis.addEventListener("voiceschanged", updateVoices);

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.removeEventListener("voiceschanged", updateVoices);
      }
    };
  }, []);

  // Sync activeSectionIndex from parent prop only when idle/not playing
  useEffect(() => {
    if (!isAutoplayActive && !isPlaying) {
      setActiveSectionIndex(currentSectionIndex);
      setCurrentContainerIndex(0);
    }
  }, [currentSectionIndex, isAutoplayActive, isPlaying]);

  const currentSection = sections[activeSectionIndex];

  const syncAudioScroll = useCallback((progressRatio) => {
    if (typeof document === "undefined") return;
    const scrollContainer = document.getElementById("scrollytell-content");
    const sectionEl =
      document.getElementById(`section-${activeSectionIndex}`) ||
      document.getElementById(`section-${activeSectionIndex + 1}`) ||
      document.getElementById(`section-${currentSection?.order}`);
    if (!scrollContainer || !sectionEl) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const sectionRect = sectionEl.getBoundingClientRect();
    const sectionTop = sectionRect.top + scrollContainer.scrollTop - containerRect.top;
    const maxScrollSpan = Math.max(0, sectionEl.offsetHeight - scrollContainer.clientHeight);

    const targetScroll = calculateInterpolatedScrollTop({
      progressRatio,
      sectionTop,
      sectionScrollSpan: maxScrollSpan,
    });

    scrollContainer.style.scrollBehavior = "auto";
    scrollContainer.scrollTop = targetScroll;
  }, [activeSectionIndex, currentSection?.order]);

  // Initialize HTML5 Audio element for dedicated tracks
  useEffect(() => {
    if (typeof window === "undefined" || !window.Audio) return;

    const audio = new Audio();
    audio.preload = "auto";
    narrationAudioRef.current = audio;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration && audio.duration > 0) {
        const ratio = audio.currentTime / audio.duration;
        syncAudioScroll(ratio);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      setMediaError(null);
    };

    const handleEnded = () => {
      if (isAutoplayActive) {
        if (activeSectionIndex < sections.length - 1) {
          const nextIndex = activeSectionIndex + 1;
          setActiveSectionIndex(nextIndex);
          setCurrentContainerIndex(0);
          onSectionChange(nextIndex);
        } else {
          setIsAutoplayActive(false);
          setIsPlaying(false);
          setStatus("IDLE");
        }
      } else {
        setIsPlaying(false);
      }
    };

    const handleError = (e) => {
      setIsPlaying(false);
      setMediaError(e?.message || "Kunne ikke laste lydspor");
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      try {
        audio.pause();
      } catch (e) {}
      narrationAudioRef.current = null;
    };
  }, [activeSectionIndex, sections.length, isAutoplayActive, onSectionChange, syncAudioScroll]);

  // Synchronize chapter audio track on activeSection change
  useEffect(() => {
    const audio = narrationAudioRef.current;
    if (!audio) return;

    const audioUrl = currentSection?.audioTrack?.url;
    if (audioUrl) {
      const resolved = resolveAudioUrl(audioUrl);
      if (resolved && audio.src !== resolved) {
        audio.src = resolved;
        audio.currentTime = 0;
        setCurrentTime(0);
        if (isPlaying || isAutoplayActive) {
          audio.play().catch(() => {});
        }
      }
    } else {
      try {
        audio.pause();
      } catch (e) {}
      audio.src = "";
      if (!isAutoplayActive && !isPlaying) {
        setCurrentTime(0);
        setDuration(0);
      }
    }
  }, [activeSectionIndex, currentSection?.audioTrack?.url, isAutoplayActive, isPlaying]);

  // Handle speed changes on narration and tts audio
  useEffect(() => {
    const factor = SPEED_FACTORS[speed] || 1.0;
    if (narrationAudioRef.current) {
      narrationAudioRef.current.playbackRate = factor;
    }
    if (ttsAudioRef.current) {
      ttsAudioRef.current.playbackRate = factor;
    }
    if (activeVideoRef.current) {
      activeVideoRef.current.playbackRate = factor;
    }
  }, [speed]);

  // Handle mute and volume on narration and tts audio
  useEffect(() => {
    const effectiveVolume = isMuted ? 0 : volume;
    if (narrationAudioRef.current) {
      narrationAudioRef.current.muted = isMuted;
      narrationAudioRef.current.volume = effectiveVolume;
    }
    if (ttsAudioRef.current) {
      ttsAudioRef.current.muted = isMuted;
      ttsAudioRef.current.volume = effectiveVolume;
    }
    if (activeVideoRef.current) {
      activeVideoRef.current.muted = isMuted;
      activeVideoRef.current.volume = effectiveVolume;
    }
  }, [isMuted, volume]);

  const stopAllPlayback = useCallback(() => {
    stepTokenRef.current++;
    cleanupPlaybackResources({
      abortControllerRef,
      activeObjectUrlRef,
      ttsAudioRef,
      narrationAudioRef,
      activeVideoRef,
      utteranceRef,
      rafRef,
      timerRef,
      watchdogTimerRef,
      progressTimerRef,
    });
    const scrollContainer =
      typeof document !== "undefined"
        ? document.getElementById("scrollytell-content")
        : null;
    if (scrollContainer) {
      scrollContainer.style.scrollBehavior = "smooth";
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllPlayback();
    };
  }, [stopAllPlayback]);

  // Pause on manual user scroll/wheel/touch
  useEffect(() => {
    if (!isAutoplayActive && !isPlaying) return;

    const handleUserIntervention = (e) => {
      if (e.isTrusted) {
        stopAllPlayback();
        setIsAutoplayActive(false);
        setIsPlaying(false);
        setStatus("PAUSED_BY_USER");
      }
    };

    window.addEventListener("wheel", handleUserIntervention, { passive: true });
    window.addEventListener("touchmove", handleUserIntervention, { passive: true });

    return () => {
      window.removeEventListener("wheel", handleUserIntervention);
      window.removeEventListener("touchmove", handleUserIntervention);
    };
  }, [isAutoplayActive, isPlaying, stopAllPlayback]);

  // Lock detection: pause when locked, auto-resume 2s after unlock
  useEffect(() => {
    const step = evaluateAutoplayStep({
      sectionIndex: activeSectionIndex,
      sections,
      locks,
      isAutoplayActive: isAutoplayActive || isPlaying,
    });

    if (step.status === "PAUSED_LOCKED") {
      stopAllPlayback();
      setStatus("PAUSED_LOCKED");
      setIsPlaying(false);
      setIsAutoplayActive(false);
    } else if (status === "PAUSED_LOCKED" && prevLocksLengthRef.current > locks.length) {
      const resumeTimeout = setTimeout(() => {
        setIsAutoplayActive(true);
        setIsPlaying(true);
        setStatus("IDLE");
      }, 2000);
      return () => clearTimeout(resumeTimeout);
    }

    prevLocksLengthRef.current = locks.length;
  }, [locks, activeSectionIndex, sections, isAutoplayActive, isPlaying, status, stopAllPlayback]);

  const smoothScrollToElement = useCallback((targetEl, onComplete, modeOrExplicitTarget) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (typeof document === "undefined") {
      if (onComplete) onComplete();
      return;
    }
    const scrollContainer = document.getElementById("scrollytell-content");
    if (!scrollContainer || !targetEl) {
      if (onComplete) onComplete();
      return;
    }

    let targetScroll;
    if (typeof modeOrExplicitTarget === "number") {
      targetScroll = Math.max(0, Math.round(modeOrExplicitTarget));
    } else {
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();
      const targetTopInContainer = targetRect.top + scrollContainer.scrollTop - containerRect.top;

      if (modeOrExplicitTarget === "top") {
        targetScroll = Math.max(0, Math.round(targetTopInContainer));
      } else {
        targetScroll = Math.max(
          0,
          Math.round(targetTopInContainer - scrollContainer.clientHeight / 2 + targetRect.height / 2)
        );
      }
    }

    const startScroll = scrollContainer.scrollTop;
    const distance = targetScroll - startScroll;
    if (Math.abs(distance) < 5) {
      if (onComplete) onComplete();
      return;
    }

    const originalBehavior = scrollContainer.style.scrollBehavior;
    scrollContainer.style.scrollBehavior = "auto";

    const speedFactor = SPEED_FACTORS[speedRef.current] || 1.0;
    const baseDurationMs = Math.min(3500, Math.max(1800, Math.abs(distance) * 2.2));
    const durationMs = Math.round(baseDurationMs / speedFactor);
    let startTime = null;

    const animate = (timestamp) => {
      if (startTime === null) {
        startTime = timestamp;
        rafRef.current = requestAnimationFrame(animate);
        return;
      }
      const elapsed = timestamp - startTime;
      const progress = Math.min(1, elapsed / durationMs);
      const ease = 0.5 - Math.cos(progress * Math.PI) / 2;
      scrollContainer.scrollTop = Math.round(startScroll + distance * ease);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        scrollContainer.scrollTop = targetScroll;
        scrollContainer.style.scrollBehavior = originalBehavior || "smooth";
        rafRef.current = null;
        if (onComplete) onComplete();
      }
    };

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  // Container-by-container speech / reading engine
  const executeStep = useCallback(() => {
    if (!isAutoplayActive && !isPlaying) return;

    const sectionsList = sectionsRef.current || [];
    const currentSec = sectionsList[activeSectionIndex];
    if (!currentSec) {
      setIsAutoplayActive(false);
      setIsPlaying(false);
      setStatus("IDLE");
      return;
    }

    // Check lock
    const sectionIdMatch = currentSec.order ?? currentSec.id ?? activeSectionIndex;
    const isLocked = (locksRef.current || []).some(
      (l) => l.sectionId === sectionIdMatch || l.sectionId === activeSectionIndex
    );
    if (isLocked) {
      stopAllPlayback();
      setStatus("PAUSED_LOCKED");
      setIsPlaying(false);
      setIsAutoplayActive(false);
      return;
    }

    // If section has dedicated audio track, let audio player drive playback
    if (currentSec.audioTrack?.url) {
      setStatus("PLAYING_AUDIO");
      setIsPlaying(true);
      setIsAutoplayActive(true);
      if (narrationAudioRef.current) {
        narrationAudioRef.current.play().catch(() => {});
      }
      return;
    }

    // If user has not chosen preference and model is not cached, ask for consent
    if (!voicePreferenceRef.current && !modelCachedRef.current) {
      stopAllPlayback();
      setIsPlaying(false);
      setIsAutoplayActive(false);
      setIsConsentDialogOpen(true);
      setStatus("AWAITING_CONSENT");
      return;
    }

    const stepToken = ++stepTokenRef.current;
    const containers = currentSec.containers || [];
    const steps = buildStorySteps(sectionsList);
    const activeStep = steps.find(
      (s) => s.sectionIndex === activeSectionIndex && s.containerIndex === currentContainerIndex
    );

    const stepText =
      activeStep?.text !== undefined
        ? activeStep.text
        : activeStep?.hasVideo
        ? ""
        : currentSec.title || "";
    setActiveSpokenText(stepText);

    const currentSpeed = speedRef.current || "normal";
    const currentVolume = volumeRef.current ?? 1.0;
    const currentMuted = isMutedRef.current || false;

    const stepDuration = Math.max(
      6,
      Math.round(((activeStep?.durationSeconds || 6) + 2) / (SPEED_FACTORS[currentSpeed] || 1.0))
    );
    setDuration(stepDuration);
    setCurrentTime(0);

    const scrollToCurrentContainer = (onComplete) => {
      if (typeof document === "undefined") {
        if (onComplete) onComplete();
        return;
      }
      const scrollContainer = document.getElementById("scrollytell-content");
      if (!scrollContainer) {
        if (onComplete) onComplete();
        return;
      }

      // Title section / first container: don't jump halfway down if user is already at the top
      if (activeSectionIndex === 0 && currentContainerIndex === 0) {
        if (scrollContainer.scrollTop <= 120) {
          if (onComplete) onComplete();
          return;
        }
        smoothScrollToElement(scrollContainer, onComplete, 0);
        return;
      }

      const containerEl =
        scrollContainer.querySelector(`#container-${activeSectionIndex}-${currentContainerIndex}`) ||
        document.getElementById(`container-${activeSectionIndex}-${currentContainerIndex}`);
      const sectionOrder = currentSec?.order ?? activeSectionIndex;
      const sectionEl =
        containerEl?.closest?.("section") ||
        scrollContainer.querySelector(`#section-${sectionOrder}`) ||
        document.getElementById(`section-${sectionOrder}`);

      const isSingleContentSection =
        (currentSec?.type === "content" || !currentSec?.type) &&
        (currentSec?.containers?.length || 0) <= 1;

      // For single-container content sections, scroll to the top of the section
      // instead of centering a bottom-aligned container that cuts the section in half
      if (isSingleContentSection && sectionEl) {
        smoothScrollToElement(sectionEl, onComplete, "top");
        return;
      }

      const targetEl = containerEl || sectionEl;
      if (targetEl) {
        smoothScrollToElement(targetEl, onComplete);
      } else if (onComplete) {
        onComplete();
      }
    };

    const onStepFinished = () => {
      if (stepTokenRef.current !== stepToken) return;
      stepTokenRef.current++;

      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (watchdogTimerRef.current) {
        clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }

      if (currentContainerIndex < containers.length - 1) {
        setCurrentContainerIndex((prev) => prev + 1);
      } else if (activeSectionIndex < sectionsList.length - 1) {
        const nextSec = activeSectionIndex + 1;
        setActiveSectionIndex(nextSec);
        setCurrentContainerIndex(0);
        if (onSectionChangeRef.current) {
          onSectionChangeRef.current(nextSec);
        }
      } else {
        setIsAutoplayActive(false);
        setIsPlaying(false);
        setStatus("IDLE");
      }
    };

    let stepStartTime = Date.now();

    const triggerVideoPlayback = (onDone) => {
      const scrollContainer =
        typeof document !== "undefined"
          ? document.getElementById("scrollytell-content")
          : null;
      const containerEl =
        scrollContainer?.querySelector(`#container-${activeSectionIndex}-${currentContainerIndex}`) ||
        (typeof document !== "undefined"
          ? document.getElementById(`container-${activeSectionIndex}-${currentContainerIndex}`)
          : null);

      playContainerVideo({
        containerEl,
        isLooping: Boolean(activeStep?.isLoopingVideo),
        stepToken,
        stepTokenRef,
        isMutedRef,
        volumeRef,
        speedRef,
        activeVideoRef,
        setStatus,
        setCurrentTime,
        setDuration,
        setActiveSpokenText,
        timerRef,
        watchdogTimerRef,
        onDone,
      });
    };

    const handleAfterSpeech = () => {
      if (stepTokenRef.current !== stepToken) return;
      if (activeStep?.hasVideo) {
        triggerVideoPlayback(onStepFinished);
      } else {
        onStepFinished();
      }
    };

    const finishAfterMinDuration = () => {
      if (stepTokenRef.current !== stepToken) return;
      const elapsed = Date.now() - stepStartTime;
      const minStepMs = stepDuration * 1000;
      if (elapsed >= 1800) {
        timerRef.current = setTimeout(handleAfterSpeech, 1200);
      } else {
        const remainingMs = Math.max(1200, minStepMs - elapsed);
        timerRef.current = setTimeout(handleAfterSpeech, remainingMs);
      }
    };

    const fallbackToSystemOrTimer = () => {
      if (stepTokenRef.current !== stepToken) return;
      scrollToCurrentContainer(() => {
        if (stepTokenRef.current !== stepToken) return;
        setCurrentTime(0);
        stepStartTime = Date.now();

        if (progressTimerRef.current) clearInterval(progressTimerRef.current);
        progressTimerRef.current = setInterval(() => {
          if (stepTokenRef.current !== stepToken) return;
          const elapsedSec = (Date.now() - stepStartTime) / 1000;
          setCurrentTime(Math.min(stepDuration, Math.round(elapsedSec * 10) / 10));
        }, 250);

        const maxStepWaitMs = Math.max((stepDuration + 4) * 1000, 8000);
        if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = setTimeout(handleAfterSpeech, maxStepWaitMs);

        setStatus("PLAYING_TTS");
        const spoken = speakSystemUtterance({
          text: stepText,
          voiceList: voicesRef.current || [],
          speed: currentSpeed,
          volume: currentVolume,
          isMuted: currentMuted,
          onEnd: finishAfterMinDuration,
          utteranceRef,
        });
        if (!spoken) {
          setStatus("PLAYING_TIMER");
          timerRef.current = setTimeout(handleAfterSpeech, stepDuration * 1000);
        }
      });
    };

    const playNeuralSpeech = () => {
      if (stepTokenRef.current !== stepToken) return;
      scrollToCurrentContainer(() => {
        if (stepTokenRef.current !== stepToken) return;
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        playNeuralClip({
          text: stepText,
          speed: currentSpeed,
          volume: currentVolume,
          isMuted: currentMuted,
          isCached: modelCachedRef.current,
          vitsLoader: vitsLoaderRef.current,
          onProgress: (p) => {
            if (stepTokenRef.current === stepToken && !abortController.signal.aborted) {
              setDownloadProgress(p);
            }
          },
          onStatusChange: (s) => {
            if (stepTokenRef.current === stepToken) setStatus(s);
          },
          onPlaybackStart: (audioDur) => {
            if (stepTokenRef.current !== stepToken) return;
            setCurrentTime(0);
            if (audioDur && Number.isFinite(audioDur)) {
              setDuration(audioDur);
            }
            if (watchdogTimerRef.current) {
              clearTimeout(watchdogTimerRef.current);
            }
            const waitMs = Math.max(((audioDur || stepDuration) + 5) * 1000, 12000);
            watchdogTimerRef.current = setTimeout(handleAfterSpeech, waitMs);
          },
          onDone: handleAfterSpeech,
          onError: () => {
            setMediaError("Kunne ikke laste naturlig stemme. Bytter til enkel systemstemme.");
            fallbackToSystemOrTimer();
          },
          onMetadata: (dur) => {
            if (stepTokenRef.current === stepToken && dur && Number.isFinite(dur)) {
              setDuration(dur);
            }
          },
          onTimeUpdate: (cur) => {
            if (stepTokenRef.current === stepToken) {
              setCurrentTime(cur);
            }
          },
          signal: abortController.signal,
          activeObjectUrlRef,
          ttsAudioRef,
          onModelCached: () => {
            setModelCached(true);
            modelCachedRef.current = true;
            setDownloadProgress(null);
          },
        }).catch((err) => {
          if (err?.name === "AbortError" || abortController.signal.aborted) return;
          setMediaError("Kunne ikke laste naturlig stemme. Bytter til enkel systemstemme.");
          setDownloadProgress(null);
          fallbackToSystemOrTimer();
        });
      });
    };

    if (activeStep?.hasVideo && !stepText.trim()) {
      scrollToCurrentContainer(() => {
        if (stepTokenRef.current !== stepToken) return;
        triggerVideoPlayback(onStepFinished);
      });
      return;
    }

    if (voicePreferenceRef.current === "neural") {
      if (!modelCachedRef.current) {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setStatus("DOWNLOADING_MODEL");
        downloadNorwegianModel({
          onProgress: (p) => {
            if (stepTokenRef.current === stepToken && !abortController.signal.aborted) {
              setDownloadProgress(p);
            }
          },
          signal: abortController.signal,
          vitsLoader: vitsLoaderRef.current,
        })
          .then(() => {
            if (stepTokenRef.current !== stepToken || abortController.signal.aborted) return;
            setModelCached(true);
            modelCachedRef.current = true;
            setDownloadProgress(null);
            playNeuralSpeech();
          })
          .catch((err) => {
            if (err?.name === "AbortError" || abortController.signal.aborted) return;
            setMediaError("Kunne ikke laste naturlig stemme. Bytter til enkel systemstemme.");
            setDownloadProgress(null);
            fallbackToSystemOrTimer();
          });
        return;
      }

      playNeuralSpeech();
      return;
    }

    fallbackToSystemOrTimer();
  }, [
    isAutoplayActive,
    isPlaying,
    activeSectionIndex,
    currentContainerIndex,
    smoothScrollToElement,
    stopAllPlayback,
  ]);

  // Trigger step when autoplay or active step changes
  useEffect(() => {
    if (isAutoplayActive || isPlaying) {
      executeStep();
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (watchdogTimerRef.current) {
        clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, [isAutoplayActive, isPlaying, activeSectionIndex, currentContainerIndex, executeStep]);

  // Unified play/pause: both buttons control unified story playback
  const togglePlayback = useCallback(() => {
    const next = !(isPlaying || isAutoplayActive);
    if (!next) {
      stopAllPlayback();
      setIsPlaying(false);
      setIsAutoplayActive(false);
      setStatus("IDLE");
      return;
    }

    const scrollContainer =
      typeof document !== "undefined"
        ? document.getElementById("scrollytell-content")
        : null;
    if (scrollContainer && scrollContainer.scrollTop < 120) {
      setActiveSectionIndex(0);
      setCurrentContainerIndex(0);
      if (onSectionChangeRef.current) {
        onSectionChangeRef.current(0);
      }
    }

    const currentSec = sectionsRef.current?.[activeSectionIndex];
    const hasDedicatedTrack = Boolean(currentSec?.audioTrack?.url);

    if (!hasDedicatedTrack && !voicePreferenceRef.current && !modelCachedRef.current) {
      stopAllPlayback();
      setIsConsentDialogOpen(true);
      setStatus("AWAITING_CONSENT");
      return;
    }

    setIsPlaying(true);
    setIsAutoplayActive(true);
  }, [isPlaying, isAutoplayActive, activeSectionIndex, stopAllPlayback]);

  const onTogglePlay = togglePlayback;
  const onToggleAutoplay = togglePlayback;

  const onChangeSpeed = useCallback((newSpeed) => {
    setSpeed(newSpeed);
  }, []);

  const onSeek = useCallback((newTime) => {
    setCurrentTime(newTime);
    if (activeVideoRef.current) {
      activeVideoRef.current.currentTime = newTime;
    }
    if (narrationAudioRef.current?.duration) {
      narrationAudioRef.current.currentTime = newTime;
    }
    if (ttsAudioRef.current?.duration) {
      ttsAudioRef.current.currentTime = newTime;
    }
  }, []);

  const onToggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const onToggleTranscript = useCallback(() => {
    setIsTranscriptOpen((prev) => !prev);
  }, []);

  const onResumeAutoplay = useCallback(() => {
    setIsAutoplayActive(true);
    setIsPlaying(true);
    setStatus("IDLE");
  }, []);

  const onSwitchToSystemVoice = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    updateVoicePreference("system");
    setDownloadProgress(null);
    if (isPlaying || isAutoplayActive) {
      executeStep();
    }
  }, [updateVoicePreference, isPlaying, isAutoplayActive, executeStep]);

  const onToggleVoiceMode = useCallback(() => {
    stopAllPlayback();
    if (voicePreferenceRef.current === "neural") {
      updateVoicePreference("system");
      if (isPlaying || isAutoplayActive) {
        executeStep();
      }
    } else {
      if (modelCachedRef.current) {
        updateVoicePreference("neural");
        if (isPlaying || isAutoplayActive) {
          executeStep();
        }
      } else {
        setIsPlaying(false);
        setIsAutoplayActive(false);
        setIsConsentDialogOpen(true);
        setStatus("AWAITING_CONSENT");
      }
    }
  }, [stopAllPlayback, updateVoicePreference, isPlaying, isAutoplayActive, executeStep]);

  const onAcceptNeuralVoice = useCallback(() => {
    updateVoicePreference("neural");
    setIsConsentDialogOpen(false);
    const scrollContainer =
      typeof document !== "undefined"
        ? document.getElementById("scrollytell-content")
        : null;
    if (scrollContainer && scrollContainer.scrollTop < 120) {
      setActiveSectionIndex(0);
      setCurrentContainerIndex(0);
      if (onSectionChangeRef.current) {
        onSectionChangeRef.current(0);
      }
    }
    setIsPlaying(true);
    setIsAutoplayActive(true);
  }, [updateVoicePreference]);

  const onUseSystemVoice = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    updateVoicePreference("system");
    setDownloadProgress(null);
    setIsConsentDialogOpen(false);
    const scrollContainer =
      typeof document !== "undefined"
        ? document.getElementById("scrollytell-content")
        : null;
    if (scrollContainer && scrollContainer.scrollTop < 120) {
      setActiveSectionIndex(0);
      setCurrentContainerIndex(0);
      if (onSectionChangeRef.current) {
        onSectionChangeRef.current(0);
      }
    }
    setIsPlaying(true);
    setIsAutoplayActive(true);
  }, [updateVoicePreference]);

  const onCloseConsentDialog = useCallback(() => {
    setIsConsentDialogOpen(false);
    setIsPlaying(false);
    setIsAutoplayActive(false);
    setStatus("IDLE");
  }, []);

  const hasTrack = Boolean(currentSection?.audioTrack?.url);
  const trackTitle =
    currentSection?.audioTrack?.title ||
    currentSection?.title ||
    (sections.length > 0 ? `Kapittel ${activeSectionIndex + 1}` : "");
  const transcript =
    currentSection?.audioTrack?.transcript || extractSectionPlainText(currentSection);

  return {
    state: {
      isPlaying,
      isAutoplayActive,
      speed,
      currentSectionIndex: activeSectionIndex,
      currentContainerIndex,
      trackTitle,
      activeSpokenText,
      currentTime,
      duration,
      volume,
      isMuted,
      isTranscriptOpen,
      transcript,
      hasTrack,
      status,
      mediaError,
      downloadProgress,
      voicePreference,
      isModelCached: modelCached,
      isConsentDialogOpen,
    },
    handlers: {
      onTogglePlay,
      onToggleAutoplay,
      onChangeSpeed,
      onSeek,
      onToggleMute,
      onToggleTranscript,
      onResumeAutoplay,
      onSwitchToSystemVoice,
      onToggleVoiceMode,
      onAcceptNeuralVoice,
      onUseSystemVoice,
      onCloseConsentDialog,
    },
  };
};

/**
 * Cleanup helper for all active playback resources.
 */
function cleanupPlaybackResources({
  abortControllerRef,
  activeObjectUrlRef,
  ttsAudioRef,
  narrationAudioRef,
  activeVideoRef,
  utteranceRef,
  rafRef,
  timerRef,
  watchdogTimerRef,
  progressTimerRef,
}) {
  if (abortControllerRef.current) {
    try {
      abortControllerRef.current.abort();
    } catch {}
    abortControllerRef.current = null;
  }
  if (activeObjectUrlRef.current) {
    try {
      if (typeof URL !== "undefined" && URL.revokeObjectURL) {
        URL.revokeObjectURL(activeObjectUrlRef.current);
      }
    } catch {}
    activeObjectUrlRef.current = null;
  }
  if (ttsAudioRef.current) {
    try {
      ttsAudioRef.current.pause();
      ttsAudioRef.current.src = "";
    } catch {}
    ttsAudioRef.current = null;
  }
  if (activeVideoRef?.current) {
    try {
      activeVideoRef.current.pause();
    } catch {}
    activeVideoRef.current = null;
  }
  if (typeof window !== "undefined" && window.speechSynthesis) {
    try {
      window.speechSynthesis.cancel();
    } catch {}
  }
  utteranceRef.current = null;
  if (rafRef.current) {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }
  if (narrationAudioRef.current) {
    try {
      narrationAudioRef.current.pause();
    } catch {}
  }
  if (timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
  if (watchdogTimerRef.current) {
    clearTimeout(watchdogTimerRef.current);
    watchdogTimerRef.current = null;
  }
  if (progressTimerRef.current) {
    clearInterval(progressTimerRef.current);
    progressTimerRef.current = null;
  }
}

/**
 * Starts container/section video playback and advances when finished.
 */
export function playContainerVideo({
  containerEl,
  isLooping = false,
  stepToken,
  stepTokenRef,
  isMutedRef,
  volumeRef,
  speedRef,
  activeVideoRef,
  setStatus,
  setCurrentTime,
  setDuration,
  setActiveSpokenText,
  timerRef,
  watchdogTimerRef,
  onDone,
}) {
  if (stepTokenRef.current !== stepToken) return;

  const videoEl =
    containerEl?.querySelector?.("video") ||
    containerEl?.closest?.("section")?.querySelector?.("video");

  if (!videoEl) {
    if (onDone) onDone();
    return;
  }

  if (videoEl.ended || (videoEl.duration && videoEl.currentTime >= videoEl.duration - 0.5)) {
    videoEl.currentTime = 0;
  }
  setCurrentTime(videoEl.currentTime || 0);

  activeVideoRef.current = videoEl;
  setStatus("PLAYING_VIDEO");
  if (setActiveSpokenText) setActiveSpokenText("");

  const playBtn = (containerEl?.closest?.("section") || containerEl)?.querySelector?.(
    'button[aria-label="Spill av video"]'
  );
  if (playBtn) {
    try {
      playBtn.click();
    } catch {}
  }

  videoEl.muted = Boolean(isMutedRef.current);
  videoEl.volume = isMutedRef.current ? 0 : (volumeRef.current ?? 1.0);
  videoEl.playbackRate = SPEED_FACTORS[speedRef.current] || 1.0;

  let cleanedUp = false;
  let loopTimer = null;

  const cleanupListeners = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    if (loopTimer) {
      clearTimeout(loopTimer);
      loopTimer = null;
    }
    videoEl.removeEventListener("loadedmetadata", onMetadata);
    videoEl.removeEventListener("canplay", onCanPlay);
    videoEl.removeEventListener("timeupdate", onTimeUpdate);
    videoEl.removeEventListener("ended", onEnded);
    videoEl.removeEventListener("seeked", onSeeked);
    videoEl.removeEventListener("ratechange", onRateChange);
    if (activeVideoRef.current === videoEl) {
      activeVideoRef.current = null;
    }
  };

  const handleFinish = () => {
    if (stepTokenRef.current !== stepToken) {
      cleanupListeners();
      return;
    }
    cleanupListeners();
    try {
      videoEl.pause();
    } catch {}
    timerRef.current = setTimeout(() => {
      if (stepTokenRef.current !== stepToken) return;
      if (onDone) onDone();
    }, 1000);
  };

  const refreshWatchdog = () => {
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
    if (videoEl.duration && Number.isFinite(videoEl.duration)) {
      const remainingSec = Math.max(0, videoEl.duration - (videoEl.currentTime || 0));
      const playbackRate = videoEl.playbackRate || 1.0;
      const waitMs = Math.max((remainingSec / playbackRate + 10) * 1000, 5000);
      watchdogTimerRef.current = setTimeout(handleFinish, waitMs);
    } else {
      watchdogTimerRef.current = setTimeout(handleFinish, 20000);
    }
  };

  const onEnded = () => {
    handleFinish();
  };

  const onTimeUpdate = () => {
    if (stepTokenRef.current !== stepToken) return;
    setCurrentTime(videoEl.currentTime || 0);
  };

  const onSeeked = () => {
    if (stepTokenRef.current !== stepToken) return;
    setCurrentTime(videoEl.currentTime || 0);
    refreshWatchdog();
  };

  const onRateChange = () => {
    if (stepTokenRef.current !== stepToken) return;
    refreshWatchdog();
  };

  const onMetadata = () => {
    if (stepTokenRef.current !== stepToken) return;
    if (videoEl.duration && Number.isFinite(videoEl.duration)) {
      setDuration(videoEl.duration);
      refreshWatchdog();

      if (isLooping || videoEl.loop) {
        if (loopTimer) clearTimeout(loopTimer);
        const loopMs = Math.max(3000, videoEl.duration * 1000);
        loopTimer = setTimeout(handleFinish, loopMs);
      }
    }
  };

  const tryPlay = () => {
    if (!videoEl.paused) return;
    const playPromise = videoEl.play?.();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        videoEl.muted = true;
        videoEl.play?.().catch(() => {});
      });
    }
  };

  const onCanPlay = () => {
    tryPlay();
  };

  videoEl.addEventListener("loadedmetadata", onMetadata);
  videoEl.addEventListener("canplay", onCanPlay);
  videoEl.addEventListener("timeupdate", onTimeUpdate);
  videoEl.addEventListener("ended", onEnded);
  videoEl.addEventListener("seeked", onSeeked);
  videoEl.addEventListener("ratechange", onRateChange);

  if (videoEl.duration && Number.isFinite(videoEl.duration)) {
    onMetadata();
  } else {
    refreshWatchdog();
  }

  if (isLooping || videoEl.loop) {
    const loopDurationMs = (videoEl.duration && Number.isFinite(videoEl.duration) ? videoEl.duration : 8) * 1000;
    loopTimer = setTimeout(handleFinish, loopDurationMs);
  }

  tryPlay();
}

/**
 * Speaks step text using the browser's native SpeechSynthesis API.
 */
function speakSystemUtterance({
  text,
  voiceList,
  speed,
  volume,
  isMuted,
  onEnd,
  utteranceRef,
}) {
  if (typeof window === "undefined" || !window.speechSynthesis || isMuted || !text.trim()) {
    return false;
  }
  try {
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel();
    }
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }

    const isLinux =
      typeof navigator !== "undefined" &&
      /linux/i.test(navigator?.platform || navigator?.userAgent || "");
    const fallbackLang = isLinux ? "nb" : "nb-NO";
    const norwegianVoice = findBestNorwegianVoice(voiceList);

    const utterance = new window.SpeechSynthesisUtterance(text.trim());
    utteranceRef.current = utterance;
    if (norwegianVoice) {
      utterance.voice = norwegianVoice;
      utterance.lang = norwegianVoice.lang || fallbackLang;
    } else {
      utterance.lang = fallbackLang;
    }
    utterance.rate = SPEED_FACTORS[speed] || 1.0;
    utterance.volume = volume;

    utterance.onend = () => {
      utteranceRef.current = null;
      onEnd();
    };
    utterance.onerror = (e) => {
      if (e?.error === "canceled" || e?.error === "interrupted") return;
      utteranceRef.current = null;
      onEnd();
    };

    window.speechSynthesis.speak(utterance);
    return true;
  } catch {
    return false;
  }
}

/**
 * Downloads model (if needed), synthesizes WAV blob, and plays audio.
 */
async function playNeuralClip({
  text,
  speed,
  volume,
  isMuted,
  isCached,
  vitsLoader,
  onProgress,
  onStatusChange,
  onPlaybackStart,
  onDone,
  onError,
  onMetadata,
  onTimeUpdate,
  signal,
  activeObjectUrlRef,
  ttsAudioRef,
  onModelCached,
}) {
  if (!isCached) {
    onStatusChange("DOWNLOADING_MODEL");
    await downloadNorwegianModel({
      onProgress,
      signal,
      vitsLoader,
    });
    if (signal?.aborted) return;
    onModelCached();
  }

  onStatusChange("SYNTHESIZING_TTS");
  const { objectUrl } = await synthesizeNorwegianSpeech(text, {
    vitsLoader,
  });
  if (signal?.aborted) {
    if (objectUrl && typeof URL !== "undefined" && URL.revokeObjectURL) {
      URL.revokeObjectURL(objectUrl);
    }
    return;
  }

  if (activeObjectUrlRef.current && typeof URL !== "undefined" && URL.revokeObjectURL) {
    URL.revokeObjectURL(activeObjectUrlRef.current);
  }
  activeObjectUrlRef.current = objectUrl;

  if (typeof Audio === "undefined") {
    throw new Error("HTML5 Audio is not supported");
  }

  const audio = new Audio(objectUrl);
  ttsAudioRef.current = audio;
  audio.playbackRate = SPEED_FACTORS[speed] || 1.0;
  if ("preservesPitch" in audio) {
    audio.preservesPitch = true;
  }
  audio.muted = isMuted;
  audio.volume = isMuted ? 0 : volume;

  audio.onloadedmetadata = () => onMetadata(audio.duration);
  audio.ontimeupdate = () => onTimeUpdate(audio.currentTime);
  audio.onended = () => {
    if (activeObjectUrlRef.current && typeof URL !== "undefined" && URL.revokeObjectURL) {
      URL.revokeObjectURL(activeObjectUrlRef.current);
      activeObjectUrlRef.current = null;
    }
    setTimeout(onDone, 900);
  };
  audio.onerror = onError;

  if (onPlaybackStart) {
    onPlaybackStart(audio.duration);
  }

  onStatusChange("PLAYING_NEURAL_TTS");
  await audio.play();
}
